let API_URL = ''; // Now updated dynamically upon UI boot
let cachedRawData = null; // Stores original JSON for instant simulation re-runs
let allParsedData = [];
let allGlobalSlots = []; // Stores all parsed slots, not just the ones under threshold
let activeYears = null; // Set of years currently selected
let isAllSelected = false;
let applyReduction = false;
let activeCalendarYear = new Date().getFullYear(); // Latest year for the local intensity chart
let calendarChartInstance = null; // Persistent reference to avoid stacking charts
let priceThreshold = 0; // Show slots below this price (p/kWh). Default 0 = negative only

const REGIONS = {
    'A': 'Eastern England',
    'B': 'East Midlands',
    'C': 'London',
    'D': 'Merseyside and N. Wales',
    'E': 'West Midlands',
    'F': 'North Eastern England',
    'G': 'North Western England',
    'H': 'Southern England',
    'J': 'South Eastern England',
    'K': 'Southern Wales',
    'L': 'Southwestern England',
    'M': 'Yorkshire',
    'N': 'Southern Scotland',
    'P': 'Northern Scotland'
};

const state = {
    loading: true,
    data: null,
    error: null
};

function toggleLoaders(active) {
    document.querySelectorAll('.loader').forEach(l => l.classList.toggle('active', active));
}

function showError(msg) {
    const toast = document.getElementById('error-toast');
    document.getElementById('error-msg').innerText = msg;
    toast.classList.remove('hidden');
    toast.classList.add('show');
    setTimeout(() => {
        toast.classList.remove('show');
        setTimeout(() => toast.classList.add('hidden'), 300);
    }, 5000);
}

// Format the date/time data
function processData(rawJson) {
    try {
        const frame = rawJson.results.A.frames[0];
        const times = frame.data.values[0];
        const prices = frame.data.values[1];

        // Filter slots based on threshold
        const matchingSlots = [];
        allGlobalSlots = [];
        const simulationCutoff = new Date('2026-04-01T00:00:00Z');

        for (let i = 0; i < times.length; i++) {
            let p = prices[i];
            const d = new Date(times[i]);

            // Retrospective simulation logic
            if (applyReduction && d < simulationCutoff) {
                p -= 3.5;
            }

            allGlobalSlots.push({ time: d, price: p });

            if (p < priceThreshold) {
                matchingSlots.push({
                    time: d,
                    price: p
                });
            }
        }

        return matchingSlots;
    } catch (e) {
        console.error("Data parsing error:", e);
        showError("Failed to parse data structure.");
        return [];
    }
}

function updateTopMetrics(data) {
    let lowestPrice = 0;
    let lowestDate = null;
    let oldestDate = null;

    data.forEach(d => {
        if (d.price < lowestPrice) {
            lowestPrice = d.price;
            lowestDate = d.time;
        }
        if (!oldestDate || d.time < oldestDate) {
            oldestDate = d.time;
        }
    });

    document.getElementById('val-total-slots').innerText = data.length.toLocaleString();

    if (data.length === 0) {
        document.getElementById('val-lowest-price').innerText = '--';
        document.getElementById('val-lowest-date').innerText = '';
        document.getElementById('val-since-date').innerText = '';
        return;
    }

    document.getElementById('val-lowest-price').innerText = lowestPrice.toFixed(2);

    const dateSpan = document.getElementById('val-lowest-date');
    if (dateSpan) {
        dateSpan.innerText = lowestDate ? lowestDate.toLocaleDateString(undefined, {
            weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        }) : '--';
    }

    const sinceSpan = document.getElementById('val-since-date');
    if (sinceSpan) {
        sinceSpan.innerText = oldestDate ? 'Since ' + oldestDate.toLocaleDateString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric'
        }) : '';
    }
}

async function fetchData() {
    toggleLoaders(true);

    // Update URL to reflect current selection
    syncStateToURL();

    try {
        const response = await fetch(API_URL);
        if (!response.ok) throw new Error("API not ok");
        cachedRawData = await response.json();
    } catch (apiError) {
        console.log("Failed to load local data", apiError);
        showError("Unable to load data. Use the python script to download region files first!");
        toggleLoaders(false);
        return;
    }

    recalculateFromCache();
}

function updateDynamicLabels() {
    const isNegativeMode = priceThreshold <= 0;
    const priceLabel = isNegativeMode ? 'Negative' : `Below ${priceThreshold}p`;
    const priceLabelLower = isNegativeMode ? 'negative' : `below ${priceThreshold}p`;
    const slotWord = isNegativeMode ? 'Negative Slots' : `Cheap Slots (${priceLabelLower})`;

    const labels = {
        'label-monthly': isNegativeMode ? 'Negative Slots per Month & Year' : `Cheap Slots (${priceLabelLower}) per Month & Year`,
        'label-table-slots': `Total ${priceLabel} Priced Slots`,
        'label-table-days': `Total Days with ${priceLabel} Prices`,
        'label-yearmonth': `${slotWord}: Year vs Month`,
        'label-yearmonth-sub': `Frequency of ${priceLabelLower} prices by year and month`,
        'label-calendar': `Yearly Distribution: Daily Slot Intensity`,
        'label-calendar-sub': `Total number of ${priceLabelLower} slots per specific day of the year`,
        'label-hour': `${slotWord}: Day vs Hour`,
        'label-hour-sub': `Average price (in pence per kWh) during ${priceLabelLower} slots`,
        'label-month': `Total Slots: Month vs Weekday`,
        'label-month-sub': `Total frequency of ${priceLabelLower} slots by month and day of the week`,
        'label-month-days': `Unique Days: Month vs Weekday`,
        'label-month-days-sub': `Number of distinct calendar days that experienced at least one ${priceLabelLower} slot`,
    };

    for (const [id, text] of Object.entries(labels)) {
        const el = document.getElementById(id);
        if (el) el.innerText = text;
    }
}

function recalculateFromCache() {
    if (!cachedRawData) return;

    toggleLoaders(true);
    updateDynamicLabels();
    allParsedData = processData(cachedRawData);

    if (allParsedData.length > 0) {
        initYearFilters();
        applyFiltersAndRender();
    } else {
        clearCharts();
        updateTopMetrics([]);
        showError("No negative prices recorded for this region!");
    }
    toggleLoaders(false);
}

function initYearFilters() {
    const years = [...new Set(allParsedData.map(d => d.time.getFullYear()))].sort((a, b) => b - a);

    // 1. Initial Load: Check URL parameters first
    const params = new URLSearchParams(window.location.search);
    const urlYears = params.get('years');

    // 2. Set default or URL-based state
    if (activeYears === null) {
        if (urlYears === 'all') {
            isAllSelected = true;
            activeYears = new Set(years);
        } else if (urlYears) {
            isAllSelected = false;
            activeYears = new Set(urlYears.split(',').map(Number));
        } else {
            // Standard Default
            activeYears = new Set(years.filter(y => y >= 2023 && y <= 2026));
            isAllSelected = false;
        }
    }

    const container = document.getElementById('year-filters');
    if (!container) return;
    container.innerHTML = '';

    const btnAll = document.createElement('button');
    btnAll.className = `filter-btn ${isAllSelected ? 'active' : ''}`;
    btnAll.innerText = 'ALL';
    btnAll.onclick = () => {
        isAllSelected = true;
        activeYears = new Set(years);
        initYearFilters();
        applyFiltersAndRender();
    };
    container.appendChild(btnAll);

    years.forEach(y => {
        const btn = document.createElement('button');
        // Show as active if ALL is selected OR specifically in the activeYears set
        btn.className = `filter-btn ${isAllSelected || activeYears.has(y) ? 'active' : ''}`;
        btn.innerText = y;
        btn.onclick = () => {
            isAllSelected = false;
            if (activeYears.has(y)) {
                activeYears.delete(y);
            } else {
                activeYears.add(y);
            }
            initYearFilters();
            applyFiltersAndRender();
        };
        container.appendChild(btn);
    });

    // POPULATE THE NEW INDEPENDENT CALENDAR SELECTOR
    const calSelector = document.getElementById('calendar-year-selector');
    if (calSelector) {
        calSelector.innerHTML = '';
        years.forEach(y => {
            const opt = document.createElement('option');
            opt.value = y;
            opt.innerText = y;
            if (parseInt(y) === activeCalendarYear) opt.selected = true;
            calSelector.appendChild(opt);
        });

        // Add event listener once
        if (!calSelector.dataset.init) {
            calSelector.dataset.init = "true";
            calSelector.addEventListener('change', (e) => {
                const loader = document.getElementById('loader-calendar');
                if (loader) loader.classList.add('active');

                activeCalendarYear = parseInt(e.target.value);

                // Small timeout to allow the browser to paint the loader before the heavy calc starts
                setTimeout(() => {
                    // Destroy old chart completely before rebuilding
                    if (calendarChartInstance) {
                        calendarChartInstance.destroy();
                        calendarChartInstance = null;
                    }
                    document.getElementById('heatmap-calendar').innerHTML = '';
                    const commonOptions = getCommonOptions();
                    renderCalendarHeatmap(allParsedData, commonOptions);
                    if (loader) loader.classList.remove('active');
                }, 50);
            });
        }
    }
}

function applyFiltersAndRender() {
    syncStateToURL();
    clearCharts();
    const filteredData = isAllSelected
        ? allParsedData
        : allParsedData.filter(d => activeYears.has(d.time.getFullYear()));

    updateTopMetrics(filteredData);
    if (filteredData.length > 0) {
        renderCharts(filteredData);
    }
}

function clearCharts() {
    document.getElementById('monthly-count-chart').innerHTML = '';
    document.getElementById('heatmap-year-month').innerHTML = '';
    document.getElementById('heatmap-calendar').innerHTML = '';
    document.getElementById('heatmap-hour').innerHTML = '';
    document.getElementById('heatmap-month').innerHTML = '';
    document.getElementById('heatmap-month-days').innerHTML = '';
    document.getElementById('chart-avg-weekday').innerHTML = '';
    document.getElementById('chart-avg-weekend').innerHTML = '';
    document.getElementById('chart-breakdown-monthly').innerHTML = '';
    document.getElementById('chart-breakdown-yearly').innerHTML = '';

    const tbody = document.getElementById('yearly-table-body');
    if (tbody) tbody.innerHTML = '';

    const tfoot = document.getElementById('yearly-table-foot');
    if (tfoot) tfoot.innerHTML = '';

    const dailybody = document.getElementById('daily-table-body');
    if (dailybody) dailybody.innerHTML = '';
}

function initApexTheme() {
    return {
        mode: 'dark',
        palette: 'palette1',
    };
}

function renderCharts(data) {
    // Shared dark theme options
    const commonOptions = {
        chart: {
            background: 'transparent',
            toolbar: { show: false },
            fontFamily: 'Inter, sans-serif'
        },
        theme: initApexTheme(),
        grid: {
            borderColor: 'rgba(255,255,255,0.05)',
            strokeDashArray: 4
        },
        tooltip: { theme: 'dark' }
    };

    renderAveragePrices(commonOptions);
    renderRateBreakdown(commonOptions);

    renderMonthlyBarChart(data, commonOptions);
    renderYearlySummaryTable(data);
    renderYearMonthHeatmap(data, commonOptions);
    // THE CALENDAR IS NOW INDEPENDENT - use allParsedData and filter internally
    renderCalendarHeatmap(allParsedData, commonOptions);
    renderHourHeatmap(data, commonOptions);
    renderMonthHeatmap(data, commonOptions);
    renderMonthDaysHeatmap(data, commonOptions);
    renderDailyCheapestOverview();
}

function getCommonOptions() {
    return {
        chart: {
            background: 'transparent',
            toolbar: { show: false },
            fontFamily: 'Inter, sans-serif'
        },
        theme: initApexTheme(),
        grid: {
            borderColor: 'rgba(255,255,255,0.05)',
            strokeDashArray: 4
        },
        tooltip: { theme: 'dark' }
    };
}

function getLatestDate(slots) {
    let latest = new Date(0);
    // Don't count "future" slots beyond today when determining latest past date
    const now = new Date();
    slots.forEach(s => {
        if (s.time > latest && s.time <= now) latest = s.time;
    });
    // Fallback if somehow all data is future or array is empty
    if (latest.getTime() === 0 && slots.length > 0) return slots[slots.length - 1].time;
    return latest;
}

function renderAveragePrices(commonOptions) {
    if (!allGlobalSlots || allGlobalSlots.length === 0) return;

    const latestDate = getLatestDate(allGlobalSlots);
    const sixMonthsAgo = new Date(latestDate);
    sixMonthsAgo.setMonth(latestDate.getMonth() - 6);

    const weekdaySums = Array(48).fill(0);
    const weekdayCounts = Array(48).fill(0);
    const weekendSums = Array(48).fill(0);
    const weekendCounts = Array(48).fill(0);

    allGlobalSlots.forEach(slot => {
        const d = slot.time;
        if (d < sixMonthsAgo || d > latestDate) return;

        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        const halfHourIndex = d.getHours() * 2 + (d.getMinutes() >= 30 ? 1 : 0);

        if (isWeekend) {
            weekendSums[halfHourIndex] += slot.price;
            weekendCounts[halfHourIndex] += 1;
        } else {
            weekdaySums[halfHourIndex] += slot.price;
            weekdayCounts[halfHourIndex] += 1;
        }
    });

    const categories = Array.from({ length: 48 }, (_, i) => {
        const h = Math.floor(i / 2).toString().padStart(2, '0');
        const m = (i % 2 === 0) ? '00' : '30';
        return `${h}:${m}`;
    });

    const weekdayAverages = weekdaySums.map((sum, i) => weekdayCounts[i] ? +(sum / weekdayCounts[i]).toFixed(2) : 0);
    const weekendAverages = weekendSums.map((sum, i) => weekendCounts[i] ? +(sum / weekendCounts[i]).toFixed(2) : 0);

    const flexibleRate = 24.5; // Typical standard rate

    const buildOptions = (dataArr, titleText) => ({
        ...commonOptions,
        series: [{ name: 'Price (p/kWh)', data: dataArr }],
        chart: { ...commonOptions.chart, type: 'bar', height: 300 },
        plotOptions: {
            bar: {
                borderRadius: 2,
                columnWidth: '80%',
                colors: {
                    ranges: [
                        { from: -100, to: flexibleRate, color: '#55a864' }, // Green
                        { from: flexibleRate, to: 30, color: '#df7e70' }, // Orange/Light Red
                        { from: 30, to: 1000, color: '#c94a64' } // Red
                    ]
                }
            }
        },
        dataLabels: { enabled: false },
        xaxis: {
            categories: categories,
            labels: {
                style: { colors: '#94a3b8' },
                formatter: (val) => val.endsWith(':00') && parseInt(val.split(':')[0]) % 2 === 0 ? val : '',
                hideOverlappingLabels: true
            },
            axisBorder: { show: false },
            axisTicks: { show: false }
        },
        yaxis: {
            title: { text: 'Price per kWh', style: { color: '#94a3b8' } },
            labels: { style: { colors: '#94a3b8' }, formatter: (val) => val.toFixed(1) + 'p' }
        },
        annotations: {
            yAxis: [{
                y: flexibleRate,
                borderColor: '#ffffff',
                strokeDashArray: 4,
                borderWidth: 2,
                label: {
                    borderColor: '#ffffff',
                    style: { color: '#fff', background: '#000', fontSize: '10px' },
                    text: 'Octopus Flexible Rate',
                    position: 'right',
                    offsetY: -5,
                    offsetX: -5
                }
            }]
        }
    });

    new ApexCharts(document.querySelector("#chart-avg-weekday"), buildOptions(weekdayAverages, 'Weekday')).render();
    new ApexCharts(document.querySelector("#chart-avg-weekend"), buildOptions(weekendAverages, 'Weekend')).render();
}

function renderRateBreakdown(commonOptions) {
    if (!allGlobalSlots || allGlobalSlots.length === 0) return;

    // Bands defined to match the image colors and typical Agile ranges
    const bands = [
        { name: '< 0p', min: -Infinity, max: 0, color: '#6b90a6' },
        { name: '0 - 15p', min: 0, max: 15, color: '#5e9672' },
        { name: '15 - 20p', min: 15, max: 20, color: '#55a864' },
        { name: '20 - 25p', min: 20, max: 25, color: '#d8b368' },
        { name: '25 - 30p', min: 25, max: 30, color: '#df7e70' },
        { name: '30 - 35p', min: 30, max: 35, color: '#c94a64' },
        { name: '> 35p', min: 35, max: Infinity, color: '#872147' }
    ];

    // --- Last 6 Months ---
    const latestDate = getLatestDate(allGlobalSlots);
    const startOfLatestMonth = new Date(latestDate.getFullYear(), latestDate.getMonth(), 1);
    
    // Generate the last 7 months (like the image) backwards from the latest month
    const monthlyGroups = [];
    for (let i = 0; i < 7; i++) {
        const d = new Date(startOfLatestMonth);
        d.setMonth(d.getMonth() - i);
        monthlyGroups.push({
            year: d.getFullYear(),
            month: d.getMonth(),
            label: d.toLocaleString('default', { month: 'long' }),
            counts: Array(bands.length).fill(0)
        });
    }

    // --- Yearly ---
    // Calculate full years, sorted ascending (oldest to newest)
    const yearsInfo = [...new Set(allGlobalSlots.map(s => s.time.getFullYear()))].sort((a, b) => a - b);
    const yearlyGroups = yearsInfo.map(y => ({
        year: y,
        label: y.toString(),
        counts: Array(bands.length).fill(0)
    }));

    // Populate counts
    allGlobalSlots.forEach(slot => {
        const p = slot.price;
        const d = slot.time;
        const bandIndex = bands.findIndex(b => p >= b.min && p < b.max);
        if (bandIndex === -1) return;

        // Add to monthly
        const mGroup = monthlyGroups.find(g => g.year === d.getFullYear() && g.month === d.getMonth());
        if (mGroup) mGroup.counts[bandIndex]++;

        // Add to yearly
        const yGroup = yearlyGroups.find(g => g.year === d.getFullYear());
        if (yGroup) yGroup.counts[bandIndex]++;
    });

    const buildStackedOptions = (groups, colors) => {
        const series = bands.map((band, bIdx) => {
            return {
                name: band.name,
                data: groups.map(g => g.counts[bIdx])
            };
        });

        return {
            ...commonOptions,
            series: series,
            chart: {
                ...commonOptions.chart,
                type: 'bar',
                stacked: true,
                stackType: '100%',
                height: 350
            },
            plotOptions: {
                bar: {
                    horizontal: true,
                    borderRadius: 0,
                },
            },
            colors: colors,
            stroke: { width: 0 },
            dataLabels: { enabled: false },
            xaxis: {
                categories: groups.map(g => g.label),
                labels: { show: false },
                axisBorder: { show: false },
                axisTicks: { show: false }
            },
            yaxis: {
                labels: { style: { colors: '#94a3b8' } }
            },
            legend: { show: false },
            tooltip: {
                y: { formatter: (val) => val + ' slots' }
            }
        };
    };

    const colors = bands.map(b => b.color);
    new ApexCharts(document.querySelector("#chart-breakdown-monthly"), buildStackedOptions(monthlyGroups, colors)).render();
    
    // The yearly groups are ascending (oldest at top of HTML element if horizontal... wait, horizontal bar chart
    // plots the first category at the top. Let's match the image:
    // "Last 6 Months": April at the top, October at the bottom (newest at top). `monthlyGroups` is already newest at index 0.
    // "Yearly": 2026 at the top, 2017 at the bottom.
    // So we should reverse `yearlyGroups` so the newest year is at index 0.
    const reversedYearlyGroups = [...yearlyGroups].reverse();
    
    new ApexCharts(document.querySelector("#chart-breakdown-yearly"), buildStackedOptions(reversedYearlyGroups, colors)).render();
}

function renderMonthlyBarChart(data, commonOptions) {
    // Structure: { "2023-01": 42, "2023-02": 10 ... }
    const monthCounts = {};

    data.forEach(slot => {
        const d = slot.time;
        // YYYY-MM format
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        monthCounts[key] = (monthCounts[key] || 0) + 1;
    });

    const sortedKeys = Object.keys(monthCounts).sort();
    const categories = sortedKeys.map(k => {
        const [y, m] = k.split('-');
        const date = new Date(parseInt(y), parseInt(m) - 1, 1);
        return date.toLocaleString('default', { month: 'short', year: '2-digit' });
    });

    const sortedCounts = sortedKeys.map(k => monthCounts[k]);
    const isNegativeMode = priceThreshold <= 0;

    const options = {
        ...commonOptions,
        series: [{
            name: isNegativeMode ? 'Negative' : `Below ${priceThreshold}p`,
            data: sortedCounts
        }],
        chart: {
            ...commonOptions.chart,
            type: 'bar',
            height: 350
        },
        plotOptions: {
            bar: {
                borderRadius: 4,
                columnWidth: '60%',
            }
        },
        dataLabels: { enabled: false },
        xaxis: {
            categories: categories,
            labels: { style: { colors: '#94a3b8' } },
            axisBorder: { show: false },
            axisTicks: { show: false }
        },
        yaxis: {
            title: { text: 'Occurrences', style: { color: '#94a3b8' } },
            labels: { style: { colors: '#94a3b8' } }
        },
        fill: {
            type: 'gradient',
            gradient: {
                shade: 'dark',
                type: 'vertical',
                gradientToColors: ['#8b5cf6'],
                stops: [0, 100]
            }
        }
    };

    new ApexCharts(document.querySelector("#monthly-count-chart"), options).render();
}

function renderYearlySummaryTable(data) {
    const yearStats = {};
    let grandOccurrences = 0;
    let grandDaysSet = new Set();

    data.forEach(slot => {
        const d = slot.time;
        const year = d.getFullYear();
        // Generate YYYY-MM-DD string as unique day key
        const dateKey = `${year}-${d.getMonth() + 1}-${d.getDate()}`;

        if (!yearStats[year]) {
            yearStats[year] = { occurrences: 0, daysSet: new Set() };
        }

        yearStats[year].occurrences += 1;
        yearStats[year].daysSet.add(dateKey);

        grandOccurrences += 1;
        grandDaysSet.add(dateKey);
    });

    const tbody = document.getElementById('yearly-table-body');
    if (!tbody) return;

    tbody.innerHTML = '';

    // Sort descending
    const sortedYears = Object.keys(yearStats).sort((a, b) => b - a);

    sortedYears.forEach(year => {
        const stats = yearStats[year];
        const tr = document.createElement('tr');

        const tdYear = document.createElement('td');
        tdYear.innerText = year;

        const tdOccurrences = document.createElement('td');
        // Format to standard comma numbers e.g. 1,000
        tdOccurrences.innerText = stats.occurrences.toLocaleString();

        const tdDays = document.createElement('td');
        // Get the size of the set representing unique days
        tdDays.innerText = stats.daysSet.size.toLocaleString();

        tr.appendChild(tdYear);
        tr.appendChild(tdOccurrences);
        tr.appendChild(tdDays);

        tbody.appendChild(tr);
    });

    const tfoot = document.getElementById('yearly-table-foot');
    if (tfoot) {
        tfoot.innerHTML = `
            <tr>
                <td style="color: var(--accent-color); font-size: 1.2rem;">Grand Total</td>
                <td>${grandOccurrences.toLocaleString()}</td>
                <td>${grandDaysSet.size.toLocaleString()}</td>
            </tr>
        `;
    }
}

function renderYearMonthHeatmap(data, commonOptions) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Find unique years in data
    const yearsInfo = [...new Set(data.map(slot => slot.time.getFullYear()))].sort((a, b) => b - a);

    // Initialize count matrix
    const countMatrix = Array(yearsInfo.length).fill(0).map(() => Array(12).fill(0));

    data.forEach(slot => {
        const yearIdx = yearsInfo.indexOf(slot.time.getFullYear());
        const monthIdx = slot.time.getMonth();
        countMatrix[yearIdx][monthIdx] += 1;
    });

    const series = yearsInfo.map((year, yIdx) => {
        return {
            name: year.toString(),
            data: months.map((monthName, mIdx) => {
                return {
                    x: monthName,
                    y: countMatrix[yIdx][mIdx]
                };
            })
        };
    });

    const options = {
        ...commonOptions,
        series: series,
        chart: {
            ...commonOptions.chart,
            height: 350,
            type: 'heatmap',
        },
        plotOptions: {
            heatmap: {
                shadeIntensity: 0.5,
                radius: 4,
                useFillColorAsStroke: false,
                colorScale: {
                    ranges: [
                        { from: 0, to: 0, name: '0', color: 'rgba(255,255,255,0.02)' },
                        { from: 1, to: 15, name: 'Rare (1-15)', color: '#0ea5e9' },
                        { from: 16, to: 40, name: 'Common (16-40)', color: '#8b5cf6' },
                        { from: 41, to: 1000, name: 'Frequent (40+)', color: '#ec4899' }
                    ]
                }
            }
        },
        dataLabels: { enabled: false },
        stroke: { width: 1, colors: ['#1e293b'] },
        xaxis: {
            labels: { style: { colors: '#94a3b8' } }
        },
        yaxis: {
            labels: { style: { colors: '#94a3b8' } }
        }
    };

    new ApexCharts(document.querySelector("#heatmap-year-month"), options).render();
}

function renderHourHeatmap(data, commonOptions) {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const hours = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);

    // Initialize matrix
    const matrix = Array(7).fill(0).map(() => Array(24).fill(0));
    const countMatrix = Array(7).fill(0).map(() => Array(24).fill(0));

    data.forEach(slot => {
        let day = slot.time.getDay(); // 0 is Sunday
        day = day === 0 ? 6 : day - 1; // Map to 0=Mon, 6=Sun
        const hour = slot.time.getHours();

        // Let's visualize the lowest (most negative) average price
        matrix[day][hour] += slot.price;
        countMatrix[day][hour] += 1;
    });

    const series = days.map((dayName, dIdx) => {
        return {
            name: dayName,
            data: hours.map((hourName, hIdx) => {
                const count = countMatrix[dIdx][hIdx];
                const avgPrice = count > 0 ? (matrix[dIdx][hIdx] / count) : 0;
                // Use absolute negative value so hotter = more negative
                return {
                    x: hourName,
                    y: count > 0 ? Math.abs(avgPrice).toFixed(2) : 0
                };
            })
        };
    }).reverse(); // Reverses so Monday sits at the top of the heatmap

    const options = {
        ...commonOptions,
        series: series,
        chart: {
            ...commonOptions.chart,
            height: 350,
            type: 'heatmap',
        },
        plotOptions: {
            heatmap: {
                shadeIntensity: 0.5,
                radius: 4,
                useFillColorAsStroke: false,
                colorScale: {
                    ranges: [
                        { from: 0, to: 0, name: 'None', color: 'rgba(255,255,255,0.02)' },
                        { from: 0.01, to: 1.5, name: 'Slightly Neg (-0 to -1.5p)', color: '#0ea5e9' },
                        { from: 1.51, to: 3.5, name: 'Moderate Neg (-1.5p to -3.5p)', color: '#8b5cf6' },
                        { from: 3.51, to: 100, name: 'Very Neg (-3.5p+)', color: '#ec4899' }
                    ]
                }
            }
        },
        tooltip: {
            y: { formatter: (value) => value > 0 ? `-${value}p` : '0p' }
        },
        dataLabels: { enabled: false },
        stroke: { width: 1, colors: ['#1e293b'] },
        xaxis: {
            labels: { style: { colors: '#94a3b8' } }
        },
        yaxis: {
            labels: { style: { colors: '#94a3b8' } }
        }
    };

    new ApexCharts(document.querySelector("#heatmap-hour"), options).render();
}

function renderMonthHeatmap(data, commonOptions) {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Initialize count matrix for month vs day of week
    const countMatrix = Array(7).fill(0).map(() => Array(12).fill(0));

    data.forEach(slot => {
        let day = slot.time.getDay();
        day = day === 0 ? 6 : day - 1;
        const month = slot.time.getMonth();

        countMatrix[day][month] += 1;
    });

    const series = days.map((dayName, dIdx) => {
        return {
            name: dayName,
            data: months.map((monthName, mIdx) => {
                return {
                    x: monthName,
                    y: countMatrix[dIdx][mIdx]
                };
            })
        };
    }).reverse(); // Reverses so Monday sits at the top

    const options = {
        ...commonOptions,
        series: series,
        chart: {
            ...commonOptions.chart,
            height: 350,
            type: 'heatmap',
        },
        plotOptions: {
            heatmap: {
                shadeIntensity: 0.5,
                radius: 4,
                useFillColorAsStroke: false,
                colorScale: {
                    ranges: [
                        { from: 0, to: 0, name: '0', color: 'rgba(255,255,255,0.02)' },
                        { from: 1, to: 15, name: 'Rare (1-15)', color: '#0ea5e9' },
                        { from: 16, to: 40, name: 'Common (16-40)', color: '#8b5cf6' },
                        { from: 41, to: 1000, name: 'Frequent (40+)', color: '#ec4899' }
                    ]
                }
            }
        },
        dataLabels: { enabled: false },
        stroke: { width: 1, colors: ['#1e293b'] },
        xaxis: {
            labels: { style: { colors: '#94a3b8' } }
        },
        yaxis: {
            labels: { style: { colors: '#94a3b8' } }
        }
    };

    new ApexCharts(document.querySelector("#heatmap-month"), options).render();
}

function renderMonthDaysHeatmap(data, commonOptions) {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Matrix of sets to store unique dates
    const uniqueDaysMatrix = Array(7).fill(0).map(() => Array(12).fill(0).map(() => new Set()));

    data.forEach(slot => {
        const d = slot.time;
        let dayOfWeek = d.getDay();
        dayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const month = d.getMonth();
        const dateString = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

        uniqueDaysMatrix[dayOfWeek][month].add(dateString);
    });

    const series = days.map((dayName, dIdx) => {
        return {
            name: dayName,
            data: months.map((monthName, mIdx) => {
                return {
                    x: monthName,
                    y: uniqueDaysMatrix[dIdx][mIdx].size
                };
            })
        };
    }).reverse(); // Reverse so Monday is at the top in ApexCharts

    const options = {
        ...commonOptions,
        series: series,
        chart: {
            ...commonOptions.chart,
            height: 350,
            type: 'heatmap',
        },
        plotOptions: {
            heatmap: {
                shadeIntensity: 0.5,
                radius: 4,
                useFillColorAsStroke: false,
                colorScale: {
                    ranges: [
                        { from: 0, to: 0, name: 'None', color: 'rgba(255,255,255,0.02)' },
                        { from: 1, to: 2, name: 'Low (1-2 days)', color: '#0ea5e9' },
                        { from: 3, to: 4, name: 'Mod (3-4 days)', color: '#8b5cf6' },
                        { from: 5, to: 1000, name: 'High (5+ days)', color: '#ec4899' }
                    ]
                }
            }
        },
        dataLabels: { enabled: false },
        stroke: { width: 1, colors: ['#1e293b'] },
        xaxis: {
            labels: { style: { colors: '#94a3b8' } }
        },
        yaxis: {
            labels: { style: { colors: '#94a3b8' } }
        }
    };

    new ApexCharts(document.querySelector("#heatmap-month-days"), options).render();
}

function renderCalendarHeatmap(allData, commonOptions) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    // 12 months (rows) vs 31 days (cols)
    const intensityMatrix = Array(12).fill(0).map(() => Array(31).fill(0));
    const slotDetailsMatrix = Array(12).fill(null).map(() => Array(31).fill(null).map(() => []));

    const now = new Date();
    // Allow up to 24 hours in the future for published day-ahead rates
    const cutoff = new Date(now.getTime() + 24 * 60 * 60 * 1000);

    // FILTER INTERNALLY BY THE LOCAL SELECTOR AND SAFETY CUTOFF
    const filteredData = allData.filter(d => {
        const yearMatch = d.time.getFullYear() === activeCalendarYear;
        const existsInReality = d.time < cutoff;
        return yearMatch && existsInReality;
    });

    filteredData.forEach(slot => {
        const d = slot.time;
        const m = d.getMonth();
        // Index by local day of month 1-31
        const dayIdx = d.getDate() - 1;
        if (dayIdx >= 0 && dayIdx < 31) {
            intensityMatrix[m][dayIdx] += 1;
            slotDetailsMatrix[m][dayIdx].push(slot);
        }
    });

    // Sort slots for tooltip display (cheapest first)
    for (let m = 0; m < 12; m++) {
        for (let d = 0; d < 31; d++) {
            slotDetailsMatrix[m][d].sort((a, b) => a.price - b.price);
        }
    }

    const series = months.map((monthName, mIdx) => {
        return {
            name: monthName,
            data: Array.from({ length: 31 }, (_, dIdx) => {
                const dayNum = dIdx + 1;
                const val = intensityMatrix[mIdx][dIdx];

                // Hide data for days that literally haven't happened yet in the current year
                // to prevent "Ghost" slots in future months
                const dateInGrid = new Date(activeCalendarYear, mIdx, dayNum);
                const isFuture = dateInGrid > now;

                return {
                    x: dayNum.toString(),
                    y: isFuture ? null : val,
                    slots: slotDetailsMatrix[mIdx][dIdx]
                };
            })
        };
    }).reverse(); // Reverse so Index is Jan -> Dec top to bottom

    const options = {
        ...commonOptions,
        series: series,
        chart: {
            ...commonOptions.chart,
            height: 480,
            type: 'heatmap',
            events: {
                dataPointSelection: function(event, chartContext, config) {
                    const data = config.w.globals.initialSeries[config.seriesIndex].data[config.dataPointIndex];
                    if (!data || data.y === null || data.y === 0 || !data.slots || data.slots.length === 0) return;
                    
                    const month = config.w.globals.initialSeries[config.seriesIndex].name;
                    const day = data.x;
                    
                    let html = `<div style="font-size: 1.25rem; font-weight: bold; margin-bottom: 1rem; color: var(--text-primary); border-bottom: 1px solid var(--glass-border); padding-bottom: 0.5rem;">${activeCalendarYear} - ${day} ${month} (${data.y} slots)</div>`;
                    html += `<div style="max-height: 50vh; overflow-y: auto; padding-right: 10px;">`;
                    data.slots.forEach(s => {
                        const time = s.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        html += `<div style="display: flex; justify-content: space-between; margin-bottom: 0.5rem; font-size: 1.1rem; border-bottom: 1px solid rgba(255,255,255,0.05); padding-bottom: 0.25rem;"><span>${time}</span><strong style="color: ${s.price <= 0 ? '#ec4899' : '#0ea5e9'}">${s.price.toFixed(2)}p</strong></div>`;
                    });
                    html += `</div>`;
                    
                    document.getElementById('modal-body').innerHTML = html;
                    document.getElementById('details-modal').classList.add('show');
                }
            }
        },
        plotOptions: {
            heatmap: {
                shadeIntensity: 0.5,
                radius: 2,
                useFillColorAsStroke: false,
                colorScale: {
                    ranges: [
                        { from: 0, to: 0, name: 'None', color: 'rgba(255,255,255,0.02)' },
                        { from: 1, to: 5, name: 'Low (1-5)', color: '#0ea5e9' },
                        { from: 6, to: 15, name: 'Mod (6-15)', color: '#8b5cf6' },
                        { from: 16, to: 48, name: 'High (16+)', color: '#ec4899' }
                    ]
                }
            }
        },
        tooltip: {
            custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
                if (data.y === null || data.y === 0 || !data.slots || data.slots.length === 0) {
                    return `<div class="apexcharts-tooltip-title" style="padding: 10px;">No matching slots</div>`;
                }
                const month = w.globals.initialSeries[seriesIndex].name;
                const day = data.x;
                let html = `<div class="apexcharts-tooltip-title" style="padding: 10px; font-weight: bold;">${activeCalendarYear} - ${day} ${month} (${data.y} slots)</div>`;
                html += `<div style="padding: 10px; max-height: 250px; overflow-y: auto;">`;
                data.slots.forEach(s => {
                    const time = s.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    html += `<div style="display: flex; justify-content: space-between; gap: 1rem; margin-bottom: 0.25rem;"><span>${time}</span><strong style="color: ${s.price <= 0 ? '#ec4899' : '#0ea5e9'}">${s.price.toFixed(2)}p</strong></div>`;
                });
                html += `</div>`;
                return html;
            }
        },
        dataLabels: { enabled: false },
        stroke: { width: 1, colors: ['#1e293b'] },
        xaxis: {
            title: { text: 'Day of Month', style: { color: '#94a3b8', fontSize: '10px' } },
            labels: { style: { colors: '#94a3b8', fontSize: '9px' } },
            position: 'top'
        },
        yaxis: {
            labels: { style: { colors: '#94a3b8' } }
        }
    };

    // Destroy previous instance if it exists
    if (calendarChartInstance) {
        calendarChartInstance.destroy();
        calendarChartInstance = null;
    }
    document.getElementById('heatmap-calendar').innerHTML = '';

    calendarChartInstance = new ApexCharts(document.querySelector("#heatmap-calendar"), options);
    calendarChartInstance.render();
}

function initRegionSelector() {
    const selector = document.getElementById('region-selector');
    const params = new URLSearchParams(window.location.search);
    const urlRegion = params.get('region');

    Object.keys(REGIONS).forEach(code => {
        const option = document.createElement('option');
        option.value = code; // Just store the code (A, B, C)
        option.innerText = `${code} - ${REGIONS[code]}`;
        if (urlRegion === code) option.selected = true;
        selector.appendChild(option);
    });

    // Determine initial API URL
    const activeCode = selector.value || 'A';
    API_URL = `./slots_${activeCode}.json`;

    selector.addEventListener('change', (e) => {
        const code = e.target.value;
        API_URL = `./slots_${code}.json`;
        fetchData();
    });
}

function initSimulationToggle() {
    const toggle = document.getElementById('toggle-reduction');
    if (!toggle) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('sim') === 'true') {
        applyReduction = true;
        toggle.checked = true;
    }

    toggle.addEventListener('change', (e) => {
        applyReduction = e.target.checked;
        recalculateFromCache();
    });
}

function initCheapestHours() {
    const input = document.getElementById('cheapest-hours');
    if (!input) return;

    let debounceTimer;
    input.addEventListener('input', (e) => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            const hours = parseFloat(e.target.value) || 2;
            document.getElementById('num-hours-label').innerText = hours;
            document.getElementById('th-hours-label').innerText = hours;
            renderDailyCheapestOverview();
        }, 300);
    });
}

function renderDailyCheapestOverview() {
    const tbody = document.getElementById('daily-table-body');
    if (!tbody || !allGlobalSlots || allGlobalSlots.length === 0) return;

    // Read user configuration
    const hours = parseFloat(document.getElementById('cheapest-hours').value) || 2;
    const numSlotsToAverage = Math.ceil(hours * 2);

    // Group by Day String
    const dailyGroups = {};

    allGlobalSlots.forEach(slot => {
        const d = slot.time;
        // Filter by active years
        if (!isAllSelected && activeYears && !activeYears.has(d.getFullYear())) {
            return;
        }

        const dateStr = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
        if (!dailyGroups[dateStr]) {
            dailyGroups[dateStr] = { date: d, slots: [] };
        }
        dailyGroups[dateStr].slots.push(slot);
    });

    // Sort days descending
    const sortedDays = Object.keys(dailyGroups).sort((a, b) => b.localeCompare(a));

    // We used to filter by priceThreshold here, but now we show all days
    // to give a complete unfiltered daily tracker.
    const filteredDays = sortedDays;

    tbody.innerHTML = '';

    // To prevent the browser hanging with thousands of rows, let's limit the table rows to 100 entries...
    // But users might want to see history. Let's show max 500.
    const maxEntries = 500;

    filteredDays.slice(0, maxEntries).forEach(dateStr => {
        const dInfo = dailyGroups[dateStr];
        // Sort slots cheapest first
        dInfo.slots.sort((a, b) => a.price - b.price);

        const lowestSlot = dInfo.slots[0];

        // Take the cheapest N slots to average
        const cheapestNSlots = dInfo.slots.slice(0, numSlotsToAverage);
        const sumPrice = cheapestNSlots.reduce((sum, slot) => sum + slot.price, 0);
        const avgPrice = sumPrice / cheapestNSlots.length;

        const tr = document.createElement('tr');

        const tdDate = document.createElement('td');
        tdDate.innerText = dInfo.date.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });

        const tdPrice = document.createElement('td');
        tdPrice.innerText = lowestSlot.price.toFixed(2);
        tdPrice.style.color = lowestSlot.price <= 0 ? '#ec4899' : '#0ea5e9';
        tdPrice.style.fontWeight = 'bold';

        const tdTime = document.createElement('td');
        tdTime.innerText = lowestSlot.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        const tdAvg = document.createElement('td');
        tdAvg.innerText = avgPrice.toFixed(2);
        tdAvg.style.color = avgPrice <= 0 ? '#ec4899' : '#0ea5e9';
        tdAvg.style.fontWeight = 'bold';

        tr.appendChild(tdDate);
        tr.appendChild(tdPrice);
        tr.appendChild(tdTime);
        tr.appendChild(tdAvg);

        tbody.appendChild(tr);
    });
}

function initPriceThreshold() {
    const input = document.getElementById('price-threshold');
    if (!input) return;

    const params = new URLSearchParams(window.location.search);
    const urlThreshold = params.get('below');
    if (urlThreshold !== null) {
        priceThreshold = parseFloat(urlThreshold);
        input.value = priceThreshold;
    }

    let debounceTimer;
    input.addEventListener('input', (e) => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            priceThreshold = parseFloat(e.target.value) || 0;
            recalculateFromCache();
        }, 400);
    });
}

function initModal() {
    const modal = document.getElementById('details-modal');
    const closeBtn = document.getElementById('close-modal');
    if (!modal || !closeBtn) return;
    
    closeBtn.addEventListener('click', () => modal.classList.remove('show'));
    modal.addEventListener('click', (e) => {
        if (e.target === modal) modal.classList.remove('show');
    });
}

function syncStateToURL() {
    const params = new URLSearchParams();

    // Save Region
    const selector = document.getElementById('region-selector');
    if (selector && selector.value) {
        params.set('region', selector.value);
    }

    // Save Years
    if (isAllSelected) {
        params.set('years', 'all');
    } else if (activeYears && activeYears.size > 0) {
        params.set('years', Array.from(activeYears).join(','));
    }

    // Save Simulation
    if (applyReduction) {
        params.set('sim', 'true');
    }

    // Save Price Threshold
    if (priceThreshold !== 0) {
        params.set('below', priceThreshold);
    }

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
}

// Boot up
document.addEventListener('DOMContentLoaded', () => {
    initRegionSelector();
    initSimulationToggle();
    initPriceThreshold();
    initCheapestHours();
    initModal();
    fetchData();
});
